/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, takeLeading, all, select, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  Asset,
  AssetOption,
  AssetOptionType,
  AsyncActionStateNoData,
  Folder,
  PurchaseOption,
  RecordingOption,
  WishlistOption,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { SagaFunctions as WishlistSagaFunctions } from 'core/store/ducks/wishlist';
import * as Data from '../data';
import * as Context from '../context';
import { getOptions } from './getOptions';
import { SeasonsData } from '../../../../core/store/ducks/pi/seasons';
import RunRecordingOption from './runOptionsRecordings';
import RunOptionsPurchase from './runOptionsPurchase';

export enum ActionType {
  REQUEST = 'programInfo/runOption',
  SUCCESS = 'programInfo/runOption/SUCCESS',
  FAIL = 'programInfo/runOption/FAIL',
}
interface Meta {
  context: Context.PiContext;
}

interface MetaRunOption extends Meta {
  option?: AssetOption;
}

// ACTIONS
type RunOption = ActionRequest<ActionType.REQUEST, MetaRunOption>;
type RunOptionFail = ActionFail<ActionType.FAIL, Meta>;
type RunOptionSuccess = ActionSuccess<ActionType.SUCCESS, undefined, Meta>;

type Actions = RunOption | RunOptionFail | RunOptionSuccess | Context.Actions;

const Actions = {
  request: (context: Context.PiContext, option?: AssetOption): RunOption => ({
    type: ActionType.REQUEST,
    meta: { context, option },
  }),
  success: (context: Context.PiContext): RunOptionSuccess => ({
    type: ActionType.SUCCESS,
    meta: { context },
  }),
  fail: (context: Context.PiContext, error: XError): RunOptionFail => ({
    type: ActionType.FAIL,
    error,
    meta: { context },
  }),
};

export const Creators = { request: Actions.request };

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState, ctx: Context.PiContext): boolean => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.actions.runOption[contextId]?.processing === true;
  },
  isProcessed: (state: StoreState, ctx: Context.PiContext): boolean => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.actions.runOption[contextId]?.processed === true;
  },
  getError: (state: StoreState, ctx: Context.PiContext): XError | undefined => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.actions.runOption[contextId]?.error;
  },
};

function* refreshAssetOptions(ctx: Context.PiContext): SagaIterator {
  const asset: Asset = yield select(Data.Selectors.asset, ctx);
  const clones: Asset[] = yield select(Data.Selectors.clones, ctx);
  const folders: Folder[] = yield select(Data.Selectors.folders, ctx);
  const seasonsData: SeasonsData | undefined = yield select(Data.Selectors.seasonsData, ctx);
  const options: AssetOption[] = yield call(getOptions, ctx, asset, clones, folders, seasonsData);
  yield put(Data.ActionCreators.update({ context: ctx, data: { options } }));
}

function* requestSaga(action: RunOption): SagaIterator {
  const { option, context } = action.meta;

  try {
    switch (option?.type) {
      case AssetOptionType.WISHLIST:
        yield call(WishlistSagaFunctions.toggleItem, option as WishlistOption);
        break;
      case AssetOptionType.RECORDING:
        yield call(RunRecordingOption, option as RecordingOption);
        break;
      case AssetOptionType.PURCHASE:
        yield call(RunOptionsPurchase, option as PurchaseOption, context);
        break;
      default:
        break;
    }

    yield call(refreshAssetOptions, context);

    yield put(Actions.success(context));
  } catch (error) {
    yield put(Actions.fail(context, XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionType.REQUEST, requestSaga)]);
}

// REDUCER
type ReducerState = Record<string, AsyncActionStateNoData>;
const initialState: ReducerState = {};

export default function RunOption(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.REQUEST: {
      const contextId = Context.getContextId(action.meta.context);
      return {
        ...state,
        [contextId]: { processing: true, processed: false },
      };
    }
    case ActionType.SUCCESS: {
      const contextId = Context.getContextId(action.meta.context);
      return {
        ...state,
        [contextId]: { processing: false, processed: true },
      };
    }
    case ActionType.FAIL: {
      const contextId = Context.getContextId(action.meta.context);
      return {
        ...state,
        [contextId]: { processing: false, processed: true, error: action.error },
      };
    }
    case Context.ActionType.CLEAR: {
      const contextId = Context.getContextId(action.meta);

      const newState = { ...state };
      delete newState[contextId];

      return newState;
    }
    default:
      return state;
  }
}
